import React, { useState } from 'react';
import { Box, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { colors, spacing, radius } from 'common/theme';
import { TextHot } from 'components/common/TextHot';
import { IconHot } from 'components/common/IconHot';
import { TooltipHot } from 'components/common/TooltipHot';
import { ButtonHot, BUTTON_MODE, BUTTON_VARIANT } from 'components/common/ButtonHot';
import { StatusBadge, BudgetBalanceBadge } from './StatusBadge';
import { COUPON_STATUS } from '../../utils/enums';
import { CouponCodeModal } from './CouponCodeModal';
import CopyIconSvg from 'assets/personal_zone/Copy.svg';
import ArchiveIconSvg from 'assets/personal_zone/Archive.svg';
import ExtractArchiveIconSvg from 'assets/personal_zone/Extract_Archive.svg';
import QrCodeIconSvg from 'assets/personal_zone/QrCode.svg';
import { generateTestId } from 'app/functions';
import { useTranslations } from 'common/hooks/useTranslations';
import { getBalanceInquiryLink } from '../../utils/functions';

const balanceInquiryLinkSx = {
  flex: 1,
  minWidth: 0,
  textDecoration: 'underline',
  cursor: 'pointer',
  '&:hover': {
    color: colors.palette.blue800,
  },
};

export const CouponItem = ({ benefit, coupon, isMultipassCoupon, isPaidCoupons = true, testID, actionsBaseTestID: externalActionsBaseTestID, onArchive, onCancel, isCancelActive, hasBorder = false }) => {
  const [copyTooltipOpen, setCopyTooltipOpen] = useState(false);
  const [copyHoverOpen, setCopyHoverOpen] = useState(false);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [archiveTooltipOpen, setArchiveTooltipOpen] = useState(false);
  const { translate } = useTranslations();
  const balanceInquiryLink = getBalanceInquiryLink(coupon, translate);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const baseTestID = testID || 'coupon-item';
  const actionsBaseTestID = externalActionsBaseTestID || generateTestId(baseTestID, 'actions');

  const handleCancelClick = (e) => {
    if (e?.stopPropagation) e.stopPropagation();
    onCancel?.(coupon, e);
  };

  const handleCopyClick = async (e) => {
    e?.stopPropagation?.();
    setCopyHoverOpen(false);
    try {
      await navigator.clipboard?.writeText(coupon.code);
      setCopyTooltipOpen(true);
      setTimeout(() => setCopyTooltipOpen(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const shouldShowCursor = benefit?.shouldRedirectToBenefitScreen;

  const showMultipassBalanceBadge =
    isMultipassCoupon && isPaidCoupons && benefit.multipassType !== 0 && coupon.price !== 0 && coupon.status !== COUPON_STATUS.FULLY_USED;
    
  const multipassRemainingBudget =
    coupon.status === COUPON_STATUS.FULLY_USED
      ? 0
      : coupon.currentBudget != null && coupon.currentBudget !== ''
        ? coupon.currentBudget
        : coupon.originalPrice;

  return (
  <Box
    testId={baseTestID}
    dir="rtl"
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? `${spacing.xl}px` : `${spacing.lg}px`,
      padding: isMobile ? `${spacing.md}px ${spacing.lg}px` : `${spacing.lg}px ${spacing.xl}px`,
      backgroundColor: colors.palette.white,
      borderRadius: `${radius.md}px`,
      cursor: shouldShowCursor ? 'pointer' : 'default',
      ...(hasBorder && {
        border: `2px solid ${colors.system.border.default}`,
      }),
    }}
  >
    <Box testId={generateTestId(baseTestID, 'top-row')} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {coupon.validity ? (
      <TextHot testID={generateTestId(baseTestID, 'validity')} size="m" color="subtle" isLtr={true}>
        {translate('mycoupon_valid_through')} {coupon.validity}
      </TextHot>
      ) : (
        <Box />
      )}
      <StatusBadge testID={generateTestId(baseTestID, 'status')} status={coupon.status} isMobile={isMobile} />
    </Box>

    <Box
      testId={generateTestId(baseTestID, 'actions-row')}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: `${spacing.sm}px`,
        minWidth: 0,
      }}
    >
      {balanceInquiryLink ? (
        <TextHot
          testID={generateTestId(baseTestID, 'balance-inquiry-link')}
          component="a"
          href={balanceInquiryLink.href}
          target="_blank"
          rel="noopener noreferrer"
          size="m"
          bold="bold"
          color="brand"
          sx={balanceInquiryLinkSx}
        >
          {balanceInquiryLink.text}
        </TextHot>
      ) : !coupon.isExternalLinkCoupon ? (
        <TextHot 
          testID={generateTestId(baseTestID, 'code')}
          size="m"
          bold="bold"
          color="primary"
          isLtr={true}
          sx={{ 
            flex: 1,
            minWidth: 0,
            wordBreak: 'break-all',
            whiteSpace: 'pre-wrap',
          }}
        >
          {translate('benefit_card_coupon_code_header')} {coupon.code}
        </TextHot>
      ) : (
        <Box sx={{ flex: 1, minWidth: 0 }} />
      )}
      <Box
        testId={generateTestId(baseTestID, 'actions')}
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          gap: `${0}px`,
        }}
      >
        <TooltipHot
          testID={generateTestId(actionsBaseTestID, 'copy-hover-tooltip')}
          title={translate('copy_coupon_hover_tooltip_title')}
          open={copyHoverOpen && !copyTooltipOpen}
          onOpen={() => setCopyHoverOpen(true)}
          onClose={() => setCopyHoverOpen(false)}
          disableHoverListener={copyTooltipOpen}
          disableFocusListener={copyTooltipOpen}
        >
          <span style={{ display: 'inline-flex' }}>
            <TooltipHot
              testID={generateTestId(actionsBaseTestID, 'copy-success-tooltip')}
              title={translate('coupon_code_copied')}
              open={copyTooltipOpen}
              disableHoverListener
              disableFocusListener
            >
              <IconButton
                testId={generateTestId(actionsBaseTestID, 'copy-btn')}
                size="small"
                onClick={handleCopyClick}
                sx={{ color: colors.system.text.subtle, padding: `${spacing.xs}px` }}
              >
                <IconHot src={CopyIconSvg} size={isMobile ? '20px' : spacing.xxl} />
              </IconButton>
            </TooltipHot>
          </span>
        </TooltipHot>
        {isPaidCoupons && (
          <TooltipHot
            testID={generateTestId(actionsBaseTestID, 'archive-hover-tooltip')}
            title={coupon.is_archived ? translate('unarchive_coupon_hover_tooltip_title') : translate('archive_coupon_hover_tooltip_title')}
            open={archiveTooltipOpen}
            onOpen={() => setArchiveTooltipOpen(true)}
            onClose={() => setArchiveTooltipOpen(false)}
          >
            <IconButton
              testId={generateTestId(actionsBaseTestID, 'archive-btn')}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setArchiveTooltipOpen(false);
                onArchive?.(coupon);
              }}
              sx={{ color: colors.system.text.subtle, padding: `${spacing.xs}px` }}
            >
              <IconHot src={coupon.is_archived ? ExtractArchiveIconSvg : ArchiveIconSvg} size={isMobile ? '20px' : spacing.xxl} />
            </IconButton>
          </TooltipHot>
        )}
        {(isPaidCoupons && coupon.status === COUPON_STATUS.SUCCESS) && (
          <ButtonHot
            testID={generateTestId(actionsBaseTestID, 'cancel-btn')}
            type="button"
            mode={BUTTON_MODE.TEXT}
            variant={BUTTON_VARIANT.PRIMARY}
            size="s"
            onClick={handleCancelClick}
            sx={{
              minWidth: 'auto',
              px: `${spacing.xs}px`,
              color: isCancelActive ? colors.system.brand.primary : colors.system.text.subtle,
              fontSize: isMobile ? 14 : 16,
            }}
          >
            {translate('cancel_coupon_button_text')}
          </ButtonHot>
        )}
        {!isPaidCoupons && (
          <TooltipHot
            testID={generateTestId(actionsBaseTestID, 'qr-hover-tooltip')}
            title={translate('coupon_modal_switch_qr')}
          >
            <IconButton
              testId={generateTestId(actionsBaseTestID, 'qrcode-btn')}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setQrModalOpen(true);
              }}
              sx={{ color: colors.system.text.subtle, padding: `${spacing.xs}px` }}
            >
              <IconHot src={QrCodeIconSvg} size={isMobile ? '20px' : spacing.xxl} />
            </IconButton>
          </TooltipHot>
        )}
      </Box>
    </Box>

    {isPaidCoupons && (
      <Box
        testId={generateTestId(baseTestID, 'price-row')}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: `${spacing.sm}px`,
          padding: `${spacing.xs}px 0`,
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'center',
            flexWrap: 'wrap',
            flex: 1,
            minWidth: 0,
            gap: `${spacing.xs}px`,
          }}
        >
          <TextHot testID={generateTestId(baseTestID, 'price-text')} size="m" color="subtle" sx={{ padding: `${spacing.xs}px 0` }} isLtr={true}>
            {translate('my_coupons_coupons_table_header_coupon_value')}:
          </TextHot>
            <TextHot testID={generateTestId(baseTestID, 'price')} size="m" color="subtle" sx={{ padding: `${spacing.xs}px 0` }} isLtr={true}>
              ₪{coupon.price}
            </TextHot>
            <TextHot testID={generateTestId(baseTestID, 'original-price-text')} size="m" color="subtle" isLtr={true}>
              <span style={{ textDecoration:  'line-through', lineHeight: 1.2 }}>₪{coupon.originalPrice}</span>
            </TextHot>
        </Box>
        {showMultipassBalanceBadge && (
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', alignSelf: 'center' }}>
            <BudgetBalanceBadge
              testID={generateTestId(actionsBaseTestID, 'budget-balance')}
              status={coupon.status}
              remainingAmount={multipassRemainingBudget}
              isMobile={isMobile}
            />
          </Box>
        )}
      </Box>
    )}


    {(coupon.orderDate || coupon.orderNumber) && (
      <Box testId={generateTestId(baseTestID, 'order-info-row')} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: `${spacing.md}px`,  flexWrap: 'wrap' }}>
        {coupon.orderDate && (
          <TextHot testID={generateTestId(baseTestID, 'order-date')} size="m" color="subtle">
            {isPaidCoupons ? translate('order_date_label_text') : translate('order_date_downloaded_label_text')} {coupon.orderDate}
          </TextHot>
        )}
        {coupon.orderNumber && (
          <Box testId={generateTestId(baseTestID, 'order-number-container')} sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: `${spacing.md}px` , flexShrink: 0 , padding: `${spacing.md}px 0` }}>
          <TextHot testID={generateTestId(baseTestID, 'order-number')} size="m" color="subtle">
              {translate('my_coupons_order_number')} {coupon.orderNumber}
            </TextHot>
          </Box>
        )}
      </Box>
    )}

    <CouponCodeModal
      open={qrModalOpen}
      onClose={() => setQrModalOpen(false)}
      couponCode={coupon.code}
      testID={generateTestId(baseTestID, 'qr-modal')}
    />
  </Box>
  );
};

export default CouponItem;


